import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { formatCurrency, formatDateTime, formatDate } from '../../utils/formatters';
import {
  Users,
  UserPlus,
  Banknote,
  AlertTriangle,
  DoorOpen,
  Loader2,
  CreditCard,
  ImageOff,
  FileWarning,
  Cake,
  ShieldOff,
  Send,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const MONTH_NAMES = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const statCards = [
  { key: 'total_members', label: 'Gesamtmitglieder', icon: Users, bgColor: 'bg-gs-yellow/10', iconColor: 'text-gs-yellow', to: '/uyeler' },
  { key: 'pending_applications', label: 'Neue Mitgliedsanträge', icon: UserPlus, bgColor: 'bg-gs-red/10', iconColor: 'text-gs-red', to: '/basvurular' },
  { key: 'payments_to_check', label: 'Zahlungen zu prüfen', icon: CreditCard, bgColor: 'bg-blue-50', iconColor: 'text-blue-600', to: '/odemeler' },
  { key: 'total_dues_collected', label: 'Einnahmen diesen Monat', icon: Banknote, bgColor: 'bg-green-50', iconColor: 'text-green-600', isCurrency: true },
  { key: 'members_with_debt', label: 'Mitglieder mit Schulden', icon: AlertTriangle, bgColor: 'bg-red-50', iconColor: 'text-red-600', to: '/aidatlar' },
  { key: 'members_without_photo', label: 'Ohne Profilbild', icon: ImageOff, bgColor: 'bg-orange-50', iconColor: 'text-orange-600', to: '/uyeler?special=no_photo' },
  { key: 'members_missing_data', label: 'Fehlende Mitgliedsdaten', icon: FileWarning, bgColor: 'bg-amber-50', iconColor: 'text-amber-600', to: '/uyeler?special=missing_data' },
  { key: 'birthdays_today', label: 'Geburtstage heute', icon: Cake, bgColor: 'bg-pink-50', iconColor: 'text-pink-600', to: '/uyeler?special=birthday_today' },
  { key: 'entry_blocked_count', label: 'Eintrittssperre', icon: ShieldOff, bgColor: 'bg-gray-100', iconColor: 'text-gray-600', to: '/uyeler?special=blocked' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [sendingBirthdayId, setSendingBirthdayId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [statsRes, bdRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/birthdays').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setBirthdays(Array.isArray(bdRes.data) ? bdRes.data : []);
    } catch (err) {
      setError('Dashboard-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const sendBirthdayMail = async (m) => {
    setSendingBirthdayId(m.id);
    try {
      await api.post(`/members/${m.id}/send-birthday`);
      toast.success(`Geburtstagsmail an ${m.first_name} gesendet`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Geburtstagsmail fehlgeschlagen');
    } finally {
      setSendingBirthdayId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards (tiklanabilir -> ilgili sayfaya gider) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          const value = stats?.[card.key] ?? 0;
          const inner = (
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg shrink-0 ${card.bgColor}`}>
                <Icon size={20} className={card.iconColor} />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  {card.isCurrency ? formatCurrency(value) : value}
                </p>
                <p className="text-xs text-gray-500 truncate">{card.label}</p>
              </div>
            </div>
          );
          return card.to ? (
            <button
              key={card.key}
              type="button"
              onClick={() => navigate(card.to)}
              className="text-left bg-white rounded-xl border border-gray-200 p-3 sm:p-4 hover:shadow-md hover:border-gs-red/40 transition-all"
            >
              {inner}
            </button>
          ) : (
            <div key={card.key} className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
              {inner}
            </div>
          );
        })}
      </div>

      {/* Madde 24: Anstehende Geburtstage (heute + 7 Tage) */}
      {birthdays.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-200">
            <Cake size={20} className="text-pink-600" />
            <h3 className="text-lg font-semibold text-gray-900">Anstehende Geburtstage</h3>
            <span className="text-sm text-gray-400">(heute &amp; nächste 7 Tage)</span>
          </div>
          <div className="divide-y divide-gray-100">
            {birthdays.map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-5 py-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${m.is_today ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-500'}`}>
                  {m.first_name?.[0] || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {m.first_name} {m.last_name}
                    {m.is_today ? <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">heute 🎂</span> : null}
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(m.birth_date)} · wird {Number(m.age) + (m.is_today ? 0 : 1)} Jahre</p>
                </div>
                <button
                  onClick={() => sendBirthdayMail(m)}
                  disabled={!m.email || sendingBirthdayId === m.id}
                  title={m.email ? 'Geburtstagsmail senden' : 'Keine E-Mail-Adresse'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-pink-700 border border-pink-200 rounded-lg hover:bg-pink-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  {sendingBirthdayId === m.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Glückwunsch
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's entries */}
      {stats?.entries_today !== undefined && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-1">
            <DoorOpen size={20} className="text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Heutige Eintritte
            </h3>
          </div>
          <p className="text-3xl font-bold text-indigo-600">
            {stats.entries_today}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent entries table */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Letzte Eintritte
          </h3>
          {stats?.recent_entries?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">
                      Mitglied
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">
                      Datum
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_entries.slice(0, 5).map((entry, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="py-2.5 px-3 text-gray-900">
                        {entry.first_name} {entry.last_name}
                      </td>
                      <td className="py-2.5 px-3 text-gray-500">
                        {formatDateTime(entry.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Noch keine Eintrittsprotokolle vorhanden</p>
          )}
        </div>

        {/* Monthly collection chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Monatliche Einnahmen
          </h3>
          {stats?.monthly_collection?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250} minHeight={200}>
              <BarChart data={stats.monthly_collection}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value) => MONTH_NAMES[value] || value}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), 'Einnahmen']}
                  labelFormatter={(label) => MONTH_NAMES[label] || label}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                  }}
                />
                <Bar
                  dataKey="total"
                  fill="#FDB913"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400">Keine Daten vorhanden</p>
          )}
        </div>
      </div>
    </div>
  );
}
